import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { PlanItem, PlanItemDetail, PlanItemName } from "./types";
import { ReactNode, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";

export const PlanEditorItem = function <T = object>({
  item,
  removeItem,
  onSecondaryClick,
  name,
  detail,
  setSelectedItem,
  children,
}: {
  item: PlanItem<T>;
  removeItem?: () => void;
  onSecondaryClick?: (item: PlanItem<T>, target: HTMLButtonElement) => void;
  name: PlanItemName;
  detail?: PlanItemDetail<T>;
  setSelectedItem?: (item: PlanItem<T>) => void;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  return (
    <Box
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      sx={(theme) => ({
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      })}
    >
      <Grid
        container
        // Used as a query selector to focus the heats input automatically
        className="plan-editor-item"
        sx={{ height: 50 }}
      >
        <Grid
          size={{ xs: 1 }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {detail && !setSelectedItem ? (
            <IconButton
              onClick={() => {
                setExpanded(!expanded);
              }}
              size="small"
            >
              {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          ) : null}
        </Grid>
        <Grid size={{ xs: 4 }} sx={{ display: "flex", alignItems: "center" }}>
          {setSelectedItem ? (
            <Link
              variant="body1"
              color={theme.palette.text.primary}
              fontWeight="bold"
              sx={{ cursor: "pointer" }}
              data-testid="plan-item-link"
              onClick={() => {
                setSelectedItem(item);
              }}
            >
              {name(item)}
            </Link>
          ) : (
            <Typography variant="body1" sx={{ userSelect: "none" }}>
              {name(item)}
            </Typography>
          )}
        </Grid>

        <Grid
          size={{ xs: 6 }}
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {children}
        </Grid>

        <Grid
          size={{ xs: 1 }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {onSecondaryClick ? (
            <IconButton
              onClick={(event) => {
                onSecondaryClick(item, event.currentTarget);
              }}
              size="small"
            >
              {Boolean(onSecondaryClick) && hovered ? (
                <ArrowForwardIcon fontSize="small" />
              ) : null}
            </IconButton>
          ) : null}
          {removeItem ? (
            <IconButton onClick={removeItem} size="small">
              {Boolean(removeItem) && hovered ? (
                <DeleteIcon fontSize="small" />
              ) : null}
            </IconButton>
          ) : null}
        </Grid>
      </Grid>
      <Collapse in={expanded}>
        {detail && expanded ? (
          <Grid container>
            <Grid size={{ xs: 1 }} />
            <Grid
              size={{ xs: 10 }}
              sx={{
                py: 1,
              }}
            >
              {detail(item)}
            </Grid>
            <Grid size={{ xs: 1 }} />
          </Grid>
        ) : null}
      </Collapse>
    </Box>
  );
};
